import { useState, useEffect } from "react";
import { faker } from "@faker-js/faker";
import "./JsonGenerator.css";
import { getMetaData } from "../../utils/Constant";
import FAQ from "../FAQ/FAQ";

const JsonGenerator = () => {
  const faqData = [
    {
      id: 1,
      question: "Is this free to use?",
      answer:
        "Yes, completely free with no sign-up required. Unlimited generations available.",
    },
    {
      id: 2,
      question: "Is the generated data real?",
      answer:
        "No, all data is randomly generated and fictional. It looks realistic but doesn't represent real people or information.",
    },
    {
      id: 3,
      question: "Can I customize the JSON structure?",
      answer:
        "Yes, you can define your own schema with custom fields, data types, and nested objects to match your needs.",
    },
    {
      id: 4,
      question: "Can I use JSON generators for unit testing?",
      answer:
        "Perfect for unit testing - creates mock data, test fixtures, and simulates API responses for comprehensive testing.",
    },
    {
      id: 5,
      question: "How can I export or download JSON?",
      answer:
        "You can copy JSON to clipboard or download as .json files with one click using our export features.",
    },
    {
      id: 6,
      question: "Is there a limit on usage?",
      answer:
        "No daily limits, but excessive use may trigger temporary rate limiting to ensure service stability.",
    },
    {
      id: 7,
      question: "How do I contact support?",
      answer:
        "Visit our main website for a contact form or email samplefiles.dev@gmail.com for assistance.",
    },
  ];

  const [activeTab, setActiveTab] = useState("custom");

  // Custom Schema State
  const [schemaFields, setSchemaFields] = useState([
    { id: 1, key: "id", dataType: "UUID", enabled: true },
    { id: 2, key: "name", dataType: "Full Name", enabled: true },
    { id: 3, key: "user", dataType: "Object", enabled: true },
    { id: 4, key: "email", dataType: "Email", enabled: true },
  ]);

  // Predefined Data State
  const [predefinedConfig, setPredefinedConfig] = useState({
    records: 5,
    dataType: "users",
    locale: "en",
  });

  // Common Output State
  const [jsonOutput, setJsonOutput] = useState({
    id: "1d768a7e-9c2b-4f81-8b0e-3a6dde5f697b",
    name: "Jane Doe",
    user: {
      email: "jane.doe@example.com",
    },
  });

  const [numRecords, setNumRecords] = useState(1);

  const dataTypeOptions = [
    "UUID",
    "Full Name",
    "Email",
    "String",
    "Number",
    "Boolean",
    "Object",
    "Array",
    "Date",
    "Phone Number",
    "Address",
    "City",
    "Country",
  ];

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("json-generator");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description; // Use .content instead of setAttribute
  }, []);

  // Custom Schema Functions
  const generateFakeValue = (dataType, key) => {
    const generators = {
      UUID: () => faker.string.uuid(),
      "Full Name": () => faker.person.fullName(),
      Email: () => faker.internet.email(),
      String: () => faker.lorem.words(3),
      Number: () => faker.number.int({ min: 1, max: 1000 }),
      Boolean: () => faker.datatype.boolean(),
      Object: () => ({
        id: faker.string.uuid(),
        value: faker.lorem.word(),
      }),
      Array: () =>
        Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, () =>
          faker.lorem.word()
        ),
      Date: () => faker.date.recent().toISOString(),
      "Phone Number": () => faker.phone.number(),
      Address: () => faker.location.streetAddress(),
      City: () => faker.location.city(),
      Country: () => faker.location.country(),
    };

    return generators[dataType]
      ? generators[dataType]()
      : `Unknown type: ${dataType}`;
  };

  const addField = () => {
    const newField = {
      id: Date.now(),
      key: "",
      dataType: "String",
      enabled: true,
    };
    setSchemaFields([...schemaFields, newField]);
  };

  const updateField = (id, field, value) => {
    setSchemaFields(
      schemaFields.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  const removeField = (id) => {
    setSchemaFields(schemaFields.filter((f) => f.id !== id));
  };

  const toggleField = (id) => {
    setSchemaFields(
      schemaFields.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f))
    );
  };

  const generateCustomJSON = () => {
    const output = {};

    if (numRecords > 1) {
      const records = [];
      for (let i = 0; i < numRecords; i++) {
        const record = {};
        schemaFields.forEach((field) => {
          if (field.enabled && field.key.trim()) {
            if (field.dataType === "Object") {
              record[field.key] = {
                email: generateFakeValue("Email"),
                name: generateFakeValue("Full Name"),
              };
            } else {
              record[field.key] = generateFakeValue(field.dataType, field.key);
            }
          }
        });
        records.push(record);
      }
      setJsonOutput(records);
    } else {
      schemaFields.forEach((field) => {
        if (field.enabled && field.key.trim()) {
          if (field.dataType === "Object") {
            output[field.key] = {
              email: generateFakeValue("Email"),
              name: generateFakeValue("Full Name"),
            };
          } else {
            output[field.key] = generateFakeValue(field.dataType, field.key);
          }
        }
      });
      setJsonOutput(output);
    }
  };

  // Predefined Data Functions
  const generatePredefinedJSON = () => {
    let data = [];

    for (let i = 0; i < predefinedConfig.records; i++) {
      switch (predefinedConfig.dataType) {
        case "users":
          data.push({
            id: faker.string.uuid(),
            firstName: faker.person.firstName(),
            lastName: faker.person.lastName(),
            email: faker.internet.email(),
            phone: faker.phone.number(),
            address: {
              street: faker.location.streetAddress(),
              city: faker.location.city(),
              state: faker.location.state(),
              zipCode: faker.location.zipCode(),
              country: faker.location.country(),
            },
            company: faker.company.name(),
            jobTitle: faker.person.jobTitle(),
            avatar: faker.image.avatar(),
            dateOfBirth: faker.date.birthdate().toISOString().split("T")[0],
          });
          break;

        case "products":
          data.push({
            id: faker.string.uuid(),
            name: faker.commerce.productName(),
            description: faker.commerce.productDescription(),
            price: faker.commerce.price(),
            category: faker.commerce.department(),
            image: faker.image.url(),
            inStock: faker.datatype.boolean(),
            rating: faker.number.float({ min: 1, max: 5, precision: 0.1 }),
            createdAt: faker.date.past().toISOString(),
          });
          break;

        case "posts":
          data.push({
            id: faker.string.uuid(),
            title: faker.lorem.sentence(),
            content: faker.lorem.paragraphs(3),
            author: faker.person.fullName(),
            excerpt: faker.lorem.sentence(),
            published: faker.datatype.boolean(),
            createdAt: faker.date.recent().toISOString(),
            tags: faker.helpers.arrayElements(
              ["tech", "news", "sports", "entertainment"],
              3
            ),
          });
          break;

        case "ecommerce":
          data.push({
            id: faker.string.uuid(),
            product: faker.commerce.productName(),
            price: faker.commerce.price(),
            category: faker.commerce.department(),
            brand: faker.company.name(),
            inStock: faker.datatype.boolean(),
            reviews: faker.number.int({ min: 0, max: 1000 }),
            rating: faker.number.float({ min: 1, max: 5, precision: 0.1 }),
            description: faker.commerce.productDescription(),
            images: Array.from({ length: 3 }, () => faker.image.url()),
          });
          break;

        default:
          break;
      }
    }

    setJsonOutput(data);
  };

  // Common Functions
  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(jsonOutput, null, 2));
    // Using Bootstrap toast notification would be better here
    alert("JSON copied to clipboard!");
  };

  const downloadJSON = () => {
    const dataStr = JSON.stringify(jsonOutput, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fake-data-${new Date().getTime()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearOutput = () => {
    setJsonOutput({});
  };

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-12 text-center">
          <h1 className="display-4 fw-bold text-black mb-3">
            Free JSON Generator
          </h1>
          <p className="lead text-muted">
            Create custom JSON schemas or use predefined templates with
            realistic fake data
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills nav-fill">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "custom" ? "active" : ""}`}
                onClick={() => setActiveTab("custom")}
              >
                <i className="bi bi-pencil-square me-2"></i>
                Custom Schema
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "predefined" ? "active" : ""
                }`}
                onClick={() => setActiveTab("predefined")}
              >
                <i className="bi bi-card-checklist me-2"></i>
                Predefined Templates
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="row">
        {/* Left Panel - Configuration */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title text-white mb-0">
                <i className="bi bi-gear me-2"></i>
                {activeTab === "custom"
                  ? "Schema Configuration"
                  : "Template Configuration"}
              </h3>
            </div>
            <div className="card-body">
              {activeTab === "custom" ? (
                <div className="custom-schema">
                  {/* Records Configuration */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Number of Records
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={numRecords}
                        onChange={(e) =>
                          setNumRecords(parseInt(e.target.value) || 1)
                        }
                        min="1"
                        max="100"
                      />
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                      <button
                        className="btn btn-success w-100"
                        onClick={addField}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Field
                      </button>
                    </div>
                  </div>

                  {/* Schema Table */}
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th width="60" className="text-center">
                            Active
                          </th>
                          <th>Key</th>
                          <th>Data Type</th>
                          <th width="80" className="text-center">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {schemaFields.map((field) => (
                          <tr key={field.id}>
                            <td className="text-center align-middle">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={field.enabled}
                                  onChange={() => toggleField(field.id)}
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={field.key}
                                onChange={(e) =>
                                  updateField(field.id, "key", e.target.value)
                                }
                                placeholder="Enter key name"
                              />
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={field.dataType}
                                onChange={(e) =>
                                  updateField(
                                    field.id,
                                    "dataType",
                                    e.target.value
                                  )
                                }
                              >
                                {dataTypeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="text-center align-middle">
                              <button
                                onClick={() => removeField(field.id)}
                                className="btn btn-danger btn-sm"
                                title="Remove field"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Generate Button */}
                  <div className="mt-3">
                    <button
                      onClick={generateCustomJSON}
                      className="btn btn-primary w-100 py-2"
                    >
                      <i className="bi bi-magic me-2"></i>
                      Generate Custom JSON
                    </button>
                  </div>
                </div>
              ) : (
                <div className="predefined-data">
                  {/* Predefined Configuration */}
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Data Type</label>
                      <select
                        className="form-select"
                        value={predefinedConfig.dataType}
                        onChange={(e) =>
                          setPredefinedConfig({
                            ...predefinedConfig,
                            dataType: e.target.value,
                          })
                        }
                      >
                        <option value="users">Users Data</option>
                        <option value="products">Products Data</option>
                        <option value="posts">Blog Posts</option>
                        <option value="ecommerce">E-commerce Products</option>
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">
                        Number of Records
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={predefinedConfig.records}
                        onChange={(e) =>
                          setPredefinedConfig({
                            ...predefinedConfig,
                            records: parseInt(e.target.value) || 1,
                          })
                        }
                        min="1"
                        max="1000"
                      />
                    </div>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={generatePredefinedJSON}
                    className="btn btn-primary w-100 py-2 mb-4"
                  >
                    <i className="bi bi-lightning me-2"></i>
                    Generate Data
                  </button>

                  {/* Template Information */}
                  <div className="card bg-light-json">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        Available Templates
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6 mb-2">
                          <span className="badge bg-primary me-1">Users</span>
                          <small>Personal information</small>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="badge bg-success me-1">
                            Products
                          </span>
                          <small>E-commerce data</small>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="badge bg-info me-1">Blog Posts</span>
                          <small>Article content</small>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="badge bg-warning me-1">
                            E-commerce
                          </span>
                          <small>Complete listings</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - JSON Output */}
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
              <h3 className="card-title text-white mb-0">
                <i className="bi bi-code-slash me-2"></i>
                JSON Output
              </h3>
              <div className="btn-group">
                <button
                  onClick={copyToClipboard}
                  className="btn btn-light btn-sm"
                  title="Copy to clipboard"
                >
                  <i className="bi bi-clipboard me-1"></i>
                  Copy
                </button>
                <button
                  onClick={downloadJSON}
                  className="btn btn-light btn-sm"
                  title="Download JSON"
                >
                  <i className="bi bi-download me-1"></i>
                  Download
                </button>
                <button
                  onClick={clearOutput}
                  className="btn btn-light btn-sm"
                  title="Clear output"
                >
                  <i className="bi bi-trash me-1"></i>
                  Clear
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="json-output-container">
                <pre className="json-output p-3 mb-0">
                  {JSON.stringify(jsonOutput, null, 2)}
                </pre>
              </div>
            </div>
            <div className="card-footer">
              <div className="row text-center">
                <div className="col-6">
                  <small className="text-muted">Records Generated</small>
                  <div className="fw-bold text-primary">
                    {Array.isArray(jsonOutput)
                      ? jsonOutput.length
                      : Object.keys(jsonOutput).length > 0
                      ? 1
                      : 0}
                  </div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Data Type</small>
                  <div className="fw-bold text-success">
                    {Array.isArray(jsonOutput) ? "Array" : "Object"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <div className="h5 mb-1 text-primary">
                    {schemaFields.length}
                  </div>
                  <small className="text-muted">Custom Fields</small>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <div className="h5 mb-1 text-success">
                    {dataTypeOptions.length}
                  </div>
                  <small className="text-muted">Data Types</small>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <div className="h5 mb-1 text-info">4</div>
                  <small className="text-muted">Predefined Templates</small>
                </div>
                <div className="col-md-3">
                  <div className="h5 mb-1 text-warning">Unlimited</div>
                  <small className="text-muted">Possibilities</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <section className="bg-white py-5 shadow-sm">
        <div className="container">
          {/* <div className="text-center mb-4">
            <i
              className="bi bi-image-fill text-primary"
              style={{ fontSize: "3rem" }}
            ></i>
          </div> */}
          <h2 className="display-6 text-center fw-bold mb-4">
            What is a Dummy JSON Generator?
          </h2>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="p-4 bg-light rounded-4 shadow-sm">
                <p className="lead text-center mb-3">
                  A <strong>Dummy JSON Generator</strong> is an online tool,
                  API, or library that creates fake but structurally valid JSON
                  data. Instead of connecting to a real database, you can use
                  these tools to instantly get placeholder data that mimics the
                  kind of data your application will eventually use.
                </p>
                <p className="lead text-center">
                  Think of it as a "Lorem Ipsum" for developers, but instead of
                  random Latin text, it generates random names, emails,
                  addresses, product details, blog posts, and much more, all
                  wrapped in a clean JSON format.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How To Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="display-6 text-center fw-bold mb-4">
            How Do I Create a dummy JSON?
          </h2>
          <p className="lead text-center text-secondary mb-5">
            It’s really simple! Just follow these quick steps:
          </p>

          <div className="row g-4 justify-content-center">
            {/* Step 1 */}
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100 text-center p-3 hover-shadow transition">
                <div className="mb-3 text-primary">
                  <i className="bi bi-sliders2 fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2 text-dark">Choose your mode</h5>
                <p className="text-muted small mb-0">
                  • Custom Schema: Design your own structure
                  <br></br>• Predefined Templates: Use ready-made templates
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100 text-center p-3">
                <div className="mb-3 text-primary">
                  <i className="bi bi-cpu fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2 text-dark">Configure your data</h5>
                <p className="text-muted small mb-0">
                  Add fields and choose data types. Set number of records
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100 text-center p-3">
                <div className="mb-3 text-primary">
                  <i className="bi bi-cloud-arrow-down fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2 text-dark">Generate & preview</h5>
                <p className="text-muted small mb-0">
                  Click "Generate JSON". Review the output in preview panel
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100 text-center p-3">
                <div className="mb-3 text-primary">
                  <i className="bi bi-window fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2 text-dark">Export your data</h5>
                <p className="text-muted small mb-0">
                  Copy to clipboard or download as file
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ
        title={"Frequently Asked Questions"}
        subTitle={"Everything you need to know about our json generator"}
        faqData={faqData}
      />
    </div>
  );
};

export default JsonGenerator;
